import { Grid, Paper, Button, Divider } from '@mui/material';
import { Link as MuiLink } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Logo from 'app/components/Logo';
import { translations } from 'locales/i18n';
import { Company, JobOffering } from 'types/AppTypes';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { ContactInfoDisplay } from './ContactInfoDisplay';

interface Props {
  offer: JobOffering;
  studentView: boolean;
  userCanEdit: boolean;
}
const fullAddress = (company: Company) => {
  return (
    <>
      {company.street}
      <br />
      {company.zip} {company.city}
      <br />
      {company.country}
    </>
  );
};

const email = (offer: JobOffering) => {
  return !!offer.creator?.email ? offer.creator.email : offer.company.mainContact.email;
};

export const JobOfferDetailContact = ({ offer, studentView, userCanEdit }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();

  const renderCompanyContactFields = (offer: JobOffering) => {
    if (offer.company.type === 'HOCHSCHULE') {
      return <></>;
    } else {
      return (
        <>
          {offer.company.phone && (
            <ContactInfoDisplay label={t(translations.common.phone())}>
              {offer.company.phone}
            </ContactInfoDisplay>
          )}
          {offer.company.fax && (
            <ContactInfoDisplay label={t(translations.common.fax())}>
              {offer.company.fax}
            </ContactInfoDisplay>
          )}
          <ContactInfoDisplay label={t(translations.common.email_short())}>
            <MuiLink href={`mailto:${offer.company.email}`} target="_blank">
              {offer.company.email}
            </MuiLink>
          </ContactInfoDisplay>
        </>
      );
    }
  };

  return (
    <Grid component={Paper} square item xs={12} md={3} className="job-offer-detail__contact">
      <Grid container>
        <Grid item xs={12} md className="job-offer-detail__contact__company-name">
          {offer.company.name}
        </Grid>
        <Grid item xs={12} md className="job-offer-detail__contact__logo">
          <Logo path="jobmarket/logos" src={offer.company.logo} size="small" />
        </Grid>
      </Grid>
      <Grid container className="job-offer-detail__contact__company">
        <ContactInfoDisplay label={t(translations.common.address())}>
          {fullAddress(offer.company)}
        </ContactInfoDisplay>
        {renderCompanyContactFields(offer)}
        <ContactInfoDisplay label={t(translations.common.homepage())}>
          <MuiLink href={offer.company.homepage} target="_blank">
            {offer.company.homepage}
          </MuiLink>
        </ContactInfoDisplay>
        <Button
          color="primary"
          variant="outlined"
          fullWidth
          onClick={() => {
            navigate(`/companies/${offer.company.id}`, { state: { back: currentURL } });
          }}
        >
          {t(translations.pages.jobOffers.openCompanyProfile())}
        </Button>
      </Grid>
      <Divider />
      {studentView || userCanEdit ? (
        <Grid container className="job-offer-detail__contact__person">
          <Grid container className="job-offer-detail__contact__person__heading">
            {t(translations.common.contactPerson())}
          </Grid>
          <ContactInfoDisplay label={t(translations.common.name())}>
            {!!offer.creator?.firstName
              ? offer.creator.firstName
              : offer.company.mainContact.firstName}{' '}
            {!!offer.creator?.lastName
              ? offer.creator.lastName
              : offer.company.mainContact.lastName}
          </ContactInfoDisplay>
          <ContactInfoDisplay label={t(translations.common.email_short())}>
            <MuiLink href={`mailto:${email(offer)}`} target="_blank">
              {email(offer)}
            </MuiLink>
          </ContactInfoDisplay>
          {offer.company.type !== 'HOCHSCHULE' && (
            <ContactInfoDisplay label={t(translations.common.phone())}>
              {offer.company.mainContact.phone}
            </ContactInfoDisplay>
          )}
        </Grid>
      ) : undefined}
      <Divider />
      <Grid container className="job-offer-detail__contact__posted">
        {t(translations.pages.jobOffers.updatedOn())}:{' '}
        {new Date(offer.modifiedAt).toLocaleDateString()}
      </Grid>
    </Grid>
  );
};
